/**
 * AdminPersonPanel — right-side drawer showing one person's cross-system state.
 *
 * Opens when a row in AdminPeoplePage is clicked. The open person's email is
 * held in ?person=<email> URL param — back button / shared link work naturally.
 * Action buttons (Set on leave, Offboard) are stubbed; implementation in next phase.
 */
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@nu-assist/ui";
import type { PersonSummary } from "./types";
import { STATUS_LABEL, STATUS_COLOR } from "./types";
import { OffboardPanel } from "./PersonRow";
import { ActionDialog } from "./ActionDialog";
import { offboardPerson, setPersonOnLeave } from "./api";

// ---------------------------------------------------------------------------
// Shared badge color map
// ---------------------------------------------------------------------------

type CardColor = "green" | "red" | "orange" | "blue" | "yellow" | "gray";

const CARD_BADGE: Record<CardColor, string> = {
  green: "bg-green-500/15 text-green-700 dark:text-green-400",
  red: "bg-red-500/15 text-red-700 dark:text-red-400",
  orange: "bg-orange-500/15 text-orange-700 dark:text-orange-400",
  blue: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  yellow: "bg-yellow-400/15 text-yellow-700 dark:text-yellow-400",
  gray: "bg-surface text-text-secondary",
};

// ---------------------------------------------------------------------------
// System card
// ---------------------------------------------------------------------------

interface CardRow {
  label: string;
  value: string;
  /** If set, renders the value as a clickable link opening in a new tab. */
  href?: string | null;
}

interface SystemCardProps {
  title: string;
  badge: { label: string; color: CardColor };
  rows: CardRow[];
}

function SystemCard({ title, badge, rows }: SystemCardProps) {
  return (
    <div className="rounded-md border border-border bg-surface p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-label font-semibold text-text">{title}</span>
        <span className={`text-label font-medium px-2 py-0.5 rounded shrink-0 ${CARD_BADGE[badge.color]}`}>
          • {badge.label}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        {rows.map((r) => (
          <div key={r.label} className="flex gap-2 text-label items-center">
            <span className="text-text-secondary w-20 shrink-0">{r.label}</span>
            {r.href
              ? (
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent font-medium font-mono text-xs underline underline-offset-2 hover:opacity-80 break-all"
                >
                  {r.value}
                </a>
              )
              : <span className="text-text font-medium break-all">{r.value}</span>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alert banner
// ---------------------------------------------------------------------------

function AlertBanner({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3">
      <p className="text-label font-semibold text-red-600 dark:text-red-400 mb-0.5">
        Can still sign in to nu-assist.
      </p>
      <p className="text-label text-text-secondary">{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

interface AdminPersonPanelProps {
  person: PersonSummary;
  onClose: () => void;
}

export function AdminPersonPanel({ person, onClose }: AdminPersonPanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [showOffboard, setShowOffboard] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [leaveReturnDate, setLeaveReturnDate] = useState("");

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Per-system badges
  const googleBadge: { label: string; color: CardColor } =
    person.google_suspended == null
      ? { label: "not connected", color: "gray" }
      : person.google_suspended
        ? { label: "suspended", color: "red" }
        : { label: "active", color: "green" };

  const thriveBadge: { label: string; color: CardColor } =
    person.thrive_disabled == null
      ? { label: "no data", color: "gray" }
      : person.thrive_disabled
        ? { label: "disabled", color: "red" }
        : { label: "active", color: "green" };

  const hubspotBadge: { label: string; color: CardColor } =
    person.hubspot_owner_archived == null
      ? { label: "no data", color: "gray" }
      : person.hubspot_owner_archived
        ? { label: "user, no owner", color: "orange" }
        : { label: "owner", color: "green" };

  const nuAssistBadge: { label: string; color: CardColor } = person.has_logged_in
    ? { label: "signed in", color: "green" }
    : { label: "access open", color: "orange" };

  // Overall status chip
  const statusLabel = STATUS_LABEL[person.status as keyof typeof STATUS_LABEL] ?? person.status;
  const statusCss = STATUS_COLOR[person.status as keyof typeof STATUS_COLOR] ?? "";
  let statusColor: CardColor = "gray";
  if (statusCss.includes("red")) statusColor = "red";
  else if (statusCss.includes("orange")) statusColor = "orange";
  else if (statusCss.includes("yellow")) statusColor = "yellow";
  else if (statusCss.includes("blue")) statusColor = "blue";
  else if (statusCss.includes("green")) statusColor = "green";

  const showBanner =
    person.status === "offboarding_pending" &&
    (person.thrive_disabled === true || person.google_suspended === true);

  const bannerMessage = person.thrive_disabled
    ? "Thrive disabled this account. Our table still has it active, so sign-in depends only on whether the Google account is closed."
    : "Google suspended this account. Our table still has it active — check Thrive to align both systems.";

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-30 bg-black/40" aria-hidden="true" onClick={onClose} />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Person details: ${person.email}`}
        className="fixed inset-y-0 right-0 z-40 w-[560px] max-w-full bg-bg border-l border-border shadow-xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-4 border-b border-border shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-h2 text-text font-semibold truncate">{person.email}</h2>
            <p className="text-label text-text-secondary mt-0.5 capitalize">
              {person.role ?? "unknown role"}
              {person.last_checked_at && (
                <>
                  {" · "}last checked{" "}
                  {new Date(person.last_checked_at).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </>
              )}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-text-secondary hover:text-text hover:bg-surface-raised transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border shrink-0">
          {person.status !== "on_leave" && (
            <Button variant="secondary" onClick={() => setShowLeave(true)}>
              Set on leave
            </Button>
          )}
          {(person.status === "consistent" ||
            person.status === "never_signed_in" ||
            person.status === "on_leave") && (
            <Button variant="danger" onClick={() => setShowOffboard(true)}>
              Offboard
            </Button>
          )}
          <span
            className={`ml-auto text-label font-medium px-2 py-0.5 rounded ${CARD_BADGE[statusColor]}`}
          >
            {statusLabel}
          </span>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {showBanner && <AlertBanner message={bannerMessage} />}

          <div className="grid grid-cols-2 gap-3">
            <SystemCard
              title="Google Workspace"
              badge={googleBadge}
              rows={[
                {
                  label: "state",
                  value:
                    person.google_suspended == null
                      ? "unknown"
                      : person.google_suspended
                        ? "suspended"
                        : "active",
                },
                {
                  label: "aliases",
                  value:
                    person.google_aliases
                      ? (person.google_aliases as string[]).join(", ")
                      : "unknown",
                },
              ]}
            />
            <SystemCard
              title="Thrive"
              badge={thriveBadge}
              rows={[
                {
                  label: "user id",
                  value: person.thrive_user_id ?? "none",
                  href: person.thrive_user_url ?? null,
                },
                {
                  label: "status",
                  value:
                    person.thrive_disabled == null
                      ? "unknown"
                      : person.thrive_disabled
                        ? "disabled"
                        : "active",
                },
              ]}
            />
            <SystemCard
              title="HubSpot"
              badge={hubspotBadge}
              rows={[
                {
                  label: "user id",
                  value: person.hubspot_user_id ?? "none",
                  href: person.hubspot_user_url ?? null,
                },
                {
                  label: "owner",
                  value:
                    person.hubspot_owner_archived == null
                      ? "unknown"
                      : person.hubspot_owner_archived
                        ? "archived"
                        : "active",
                },
              ]}
            />
            <SystemCard
              title="nu-assist"
              badge={nuAssistBadge}
              rows={[
                {
                  label: "last seen",
                  value: person.last_sign_in_at
                    ? new Date(person.last_sign_in_at).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "never",
                },
                { label: "session", value: person.has_logged_in ? "has logged in" : "none active" },
              ]}
            />
            <SystemCard
              title="nu-advisory"
              badge={
                person.google_suspended == null
                  ? { label: "unknown", color: "gray" }
                  : person.google_suspended
                    ? { label: "suspended", color: "red" }
                    : { label: "active", color: "green" }
              }
              rows={[
                {
                  label: "access",
                  value:
                    person.google_suspended == null
                      ? "unknown"
                      : person.google_suspended
                        ? "blocked (Google suspended)"
                        : "allowed",
                },
              ]}
            />
          </div>
        </div>
      </div>
      {showOffboard && (
        <OffboardPanel
          person={person}
          onConfirm={() => offboardPerson(person.email)}
          onClose={() => setShowOffboard(false)}
        />
      )}
      {showLeave && (
        <ActionDialog
          title="Set on leave"
          description={`Set ${person.email} on leave.`}
          confirmLabel="Set on leave"
          onConfirm={() =>
            setPersonOnLeave(person.email, { return_date: leaveReturnDate || null })
          }
          onClose={() => { setShowLeave(false); setLeaveReturnDate(""); }}
        >
          <label className="block text-label text-text-secondary mb-1">
            Return date <span className="text-text-muted">(optional — leave blank for indefinite)</span>
          </label>
          <input
            type="date"
            className="w-full border border-border rounded px-3 py-1.5 text-body bg-bg text-text"
            value={leaveReturnDate}
            onChange={(e) => setLeaveReturnDate(e.target.value)}
          />
        </ActionDialog>
      )}
    </>
  );
}
